import { QueryClient } from '@tanstack/react-query'
import { CogsSelectableKeys, DirectCostUnitCost, DirectCostUnitCostPostBody } from '@/feature/financial-model/ecommerce/cogs/api/types'
import { postDirectCostsPeriodicDataBulk } from '@/feature/financial-model/ecommerce/cogs/api'
import { createQueryKey, COGS_BASE_KEY, TOTALS_KEY, resetGlobalQueryKey } from '@/api/query-keys'
import { parsePeriod, filterRowsStartingFrom } from '@/utils/sidepanel'

const getPeriodMapKey = (year: number, month: number) => `${year}-${String(month).padStart(2, `0`)}`

type ApplyDirectCostOptions = {
  value: number
  startFrom?: { year: number; month: number }
  exactPeriod?: { year: number; month: number }
  financialModelId?: string
  payload?: any
  queryClient: QueryClient
  ref?: { current: number | null }
}

export const applyDirectCostValueToRow = async ({
  value,
  startFrom,
  exactPeriod,
  financialModelId,
  payload,
  queryClient,
  ref
}: ApplyDirectCostOptions) => {
  if (!financialModelId || !payload?.directCostPeriods) return

  const mutateDataKey = payload.mutateDataKey as CogsSelectableKeys | undefined
  const periodValuesByKey = payload?.periodValuesByKey as Record<string, number> | undefined

  if (!mutateDataKey) return

  const forecastRows = payload.directCostPeriods.filter((period: DirectCostUnitCost) => period.period_type === `forecast`)

  if (!forecastRows.length) return

  const rowsToUpdate: DirectCostUnitCost[] = (
    periodValuesByKey
      ? forecastRows.filter((period: DirectCostUnitCost) => {
          const { year, month } = parsePeriod(period.period)
          if (year === undefined || month === undefined) return false
          return periodValuesByKey[getPeriodMapKey(year, month)] !== undefined
        })
      : filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)
  ) as DirectCostUnitCost[]

  if (!rowsToUpdate.length) return

  const directCostsQueryKey = createQueryKey({
    baseKey: COGS_BASE_KEY,
    keys: [`direct-costs`, financialModelId, payload.queryPeriod ?? `forecast`, payload.directCostCategoryId ?? undefined, payload.view ?? `monthly`]
  })

  const updatedPeriodIds = new Set(rowsToUpdate.map(period => period.id))

  queryClient.setQueryData(directCostsQueryKey, (current: any) => {
    if (!current) return current

    return current.map((entry: any) => ({
      ...entry,
      periods: entry.periods.map((period: any) => {
        if (!updatedPeriodIds.has(period.id)) return period

        const { year, month } = parsePeriod(period.period)
        const mappedValue = year !== undefined && month !== undefined ? periodValuesByKey?.[getPeriodMapKey(year, month)] : undefined

        return {
          ...period,
          [mutateDataKey]: mappedValue ?? value
        }
      })
    }))
  })

  const payloads: DirectCostUnitCostPostBody[] = rowsToUpdate.map(period => {
    const { year, month } = parsePeriod(period.period)
    const mappedValue = year !== undefined && month !== undefined ? periodValuesByKey?.[getPeriodMapKey(year, month)] : undefined
    const valueToApply = mappedValue ?? value

    return {
      ...(period.id ? { id: period.id } : {}),
      ...period,
      config: {
        ...period.config,
        [mutateDataKey]: {
          type: period.config[mutateDataKey]?.type ?? `absolute`,
          allowed_types: period.config[mutateDataKey]?.allowed_types ?? []
        }
      },
      [mutateDataKey]: valueToApply,
      year: year ?? 0,
      month: month ?? 0,
      period_type: period.period_type,
      view: payload.view ?? `monthly`
    } as DirectCostUnitCostPostBody
  })

  try {
    await postDirectCostsPeriodicDataBulk({ financialModelId, data: payloads })

    queryClient.invalidateQueries({ queryKey: directCostsQueryKey })
    queryClient.resetQueries({
      queryKey: createQueryKey({ baseKey: TOTALS_KEY, keys: [financialModelId, `direct-costs`] })
    })
    resetGlobalQueryKey([TOTALS_KEY])
  } catch (err) {
    console.error(`Error applying direct costs value to rows`, err)
    queryClient.invalidateQueries({ queryKey: directCostsQueryKey })
  }

  if (ref?.current) {
    window.clearTimeout(ref.current)
  }

  if (ref) {
    ref.current = window.setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: directCostsQueryKey })
      ref!.current = null
    }, 800) as unknown as number
  }
}

export default applyDirectCostValueToRow
